import React, { useState, useRef, useEffect } from 'react';
import { Notification, ArrowRight2 } from 'iconsax-reactjs';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchNotifications, fetchUnreadCount, markNotificationAsRead } from '../../redux/slices/notificationsSlice';
import './Notifications.css';

// Helper function to format date
const formatNotificationDate = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Helper to get type badge color
const getTypeBadgeClass = (type) => {
  switch (type) {
    case 'general': return 'badge-general';
    case 'news': return 'badge-news';
    case 'class': return 'badge-class';
    case 'assignment': return 'badge-assignment';
    case 'payment': return 'badge-payment';
    default: return 'badge-general';
  }
};

// Helper to get type label
const getTypeLabel = (type) => {
  switch (type) {
    case 'general': return 'General';
    case 'news': return 'News';
    case 'class': return 'Class';
    case 'assignment': return 'Assignment';
    case 'payment': return 'Payment';
    default: return type;
  }
};

function NotificationBell() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const { unreadCount, notifications, loading } = useSelector(
    (state) => state.notifications
  );

  // Fetch notifications on mount and when dropdown opens
  useEffect(() => {
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  useEffect(() => {
    if (isOpen && notifications.length === 0) {
      dispatch(fetchNotifications({ per_page: 10 }));
    }
  }, [isOpen, dispatch, notifications.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleDropdown = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (newIsOpen) {
      dispatch(fetchNotifications({ per_page: 10 }));
    }
  };

  const handleMarkAsRead = async (e, notification) => {
    e.stopPropagation();
    if (!notification.is_read) {
      await dispatch(markNotificationAsRead(notification.id));
      // Update local state immediately for instant feedback
      // and fetch fresh count from API
      dispatch(fetchUnreadCount());
      dispatch(fetchNotifications({ per_page: 10 }));
    }
  };

  const handleMarkAllAsRead = async () => {
    for (const notification of notifications) {
      if (!notification.is_read) {
        await dispatch(markNotificationAsRead(notification.id));
      }
    }
    // Fetch fresh data from API
    dispatch(fetchUnreadCount());
    dispatch(fetchNotifications({ per_page: 10 }));
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button 
        className="notification-bell-btn" 
        onClick={handleToggleDropdown}
        aria-label="Notifications"
      >
        <Notification size="22" color="#5e6278" />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <h4>Notifications</h4>
            {unreadCount > 0 && (
              <button 
                className="mark-all-read-btn"
                onClick={handleMarkAllAsRead}
                disabled={loading}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="notification-dropdown-body">
            {loading ? (
              <div className="notification-loading">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <Notification size="32" color="#c4c4c4" />
                <p>No notifications yet</p>
              </div>
            ) : (
              <ul className="notification-list">
                {notifications.map((notification) => (
                  <li 
                    key={notification.id}
                    className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
                    onClick={(e) => handleMarkAsRead(e, notification)}
                  >
                    <div className="notification-item-content">
                      <div className="notification-item-header">
                        <span className={`notification-type-badge ${getTypeBadgeClass(notification.type)}`}>
                          {getTypeLabel(notification.type)}
                        </span>
                        <span className="notification-time">
                          {formatNotificationDate(notification.created_at)}
                        </span>
                      </div>
                      <h5 className="notification-title">{notification.title}</h5>
                      <p className="notification-content">
                        {notification.content?.length > 80 
                          ? `${notification.content.substring(0, 80)}...` 
                          : notification.content}
                      </p>
                      {notification.cohort && (
                        <span className="notification-cohort">
                          {notification.cohort.name}
                        </span>
                      )}
                    </div>
                    {!notification.is_read && (
                      <span className="unread-dot"></span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-dropdown-footer">
              <button 
                className="view-all-notifications-btn"
                onClick={() => navigate('/notifications')}
              >
                View All Notifications
                <ArrowRight2 size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
